/**
 * CK | Cart upsell carousel
 * Custom element: <ck-cart-upsell>
 *
 * - Flickity carousel with the theme's motion defaults (friction 0.8, selectedAttraction 0.14)
 * - Single-variant products add to cart via /cart/add.js
 * - Multi-variant products dispatch 'ck:cart-upsell:open-picker' (handled by <ck-variant-picker>)
 * - Dismiss (X) has two modes controlled by the `data-persist-dismiss` attribute:
 *     Without: in-memory only (static Set) — reappears on page reload
 *     With:    localStorage-backed — stays hidden until product is removed and re-added
 *              (Shopify generates a new item.key on re-add, so old entries don't match)
 * - After a successful add, fires 'cart:build' — the theme re-renders cart.ajax.liquid
 *   and our Liquid filter automatically drops the product from the carousel
 */
(function () {
  if (window.customElements && customElements.get('ck-cart-upsell')) return;

  var STORAGE_KEY = 'ck-cart-upsell-dismissed';
  var MAX_DISMISSED = 30;

  function getPersistedKeys() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return [];
    }
  }

  function persistKey(key) {
    var list = getPersistedKeys();
    if (list.indexOf(key) === -1) {
      list.push(key);
      while (list.length > MAX_DISMISSED) list.shift();
    }
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (e) { /* storage full or unavailable */ }
  }

  function isKeyPersisted(key) {
    return getPersistedKeys().indexOf(key) !== -1;
  }

  class CkCartUpsell extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;

      this.itemKey = this.dataset.itemKey || '';
      this.errorString = this.dataset.strError || 'Something went wrong. Please try again.';
      this.persistDismiss = this.hasAttribute('data-persist-dismiss');

      var isDismissed = this.persistDismiss
        ? isKeyPersisted(this.itemKey)
        : CkCartUpsell.dismissedKeys.has(this.itemKey);

      if (isDismissed) {
        this.setAttribute('hidden', '');
        return;
      }

      this.carousel = this.querySelector('[data-ck-carousel]');
      this.dismissBtn = this.querySelector('[data-ck-dismiss]');
      this.live = this.querySelector('[data-ck-live]');

      this.onDismiss = this.onDismiss.bind(this);
      this.onClick = this.onClick.bind(this);

      if (this.dismissBtn) {
        this.dismissBtn.addEventListener('click', this.onDismiss);
      }
      this.addEventListener('click', this.onClick);

      this.initCarousel();
    }

    disconnectedCallback() {
      if (this.dismissBtn) {
        this.dismissBtn.removeEventListener('click', this.onDismiss);
      }
      this.removeEventListener('click', this.onClick);
      this.destroyCarousel();
    }

    initCarousel() {
      if (!this.carousel || typeof window.Flickity === 'undefined') return;

      const slides = this.carousel.querySelectorAll('[data-ck-slide]');
      const count = slides.length;
      if (count < 1) return;

      const opts = {
        cellSelector: '[data-ck-slide]',
        prevNextButtons: count > 1,
        pageDots: false,
        wrapAround: false,
        contain: true,
        groupCells: false,
        friction: 0.8,
        selectedAttraction: 0.14,
        dragThreshold: 7,
        accessibility: true,
        adaptiveHeight: false,
        rightToLeft: !!(window.theme && window.theme.config && window.theme.config.rtl)
      };

      try {
        this.flickity = new window.Flickity(this.carousel, opts);

        this.forceResize = () => {
          if (!this.flickity) return;
          var cards = this.carousel.querySelectorAll('.ck-cart-upsell__card');
          // Reset heights so we can measure natural content height
          cards.forEach(function (c) { c.style.height = ''; });
          // Find the tallest card
          var maxH = 0;
          cards.forEach(function (c) {
            if (c.offsetHeight > maxH) maxH = c.offsetHeight;
          });
          // Set all cards to the tallest height
          if (maxH > 0) {
            cards.forEach(function (c) { c.style.height = maxH + 'px'; });
          }
          this.flickity.resize();
        };

        requestAnimationFrame(this.forceResize);

        this.onCartOpen = () => {
          requestAnimationFrame(this.forceResize);
        };
        document.addEventListener('drawerOpen.CartDrawer', this.onCartOpen);
      } catch (e) {
        console.warn('[ck-cart-upsell] Flickity init failed, falling back to native scroll', e);
      }
    }

    destroyCarousel() {
      if (this.onCartOpen) {
        document.removeEventListener('drawerOpen.CartDrawer', this.onCartOpen);
        this.onCartOpen = null;
      }
      if (this.flickity) {
        try { this.flickity.destroy(); } catch (e) { /* noop */ }
        this.flickity = null;
      }
    }

    onDismiss(evt) {
      evt.preventDefault();
      if (this.persistDismiss) {
        persistKey(this.itemKey);
      } else {
        CkCartUpsell.dismissedKeys.add(this.itemKey);
      }
      this.setAttribute('hidden', '');
      this.destroyCarousel();
    }

    onClick(evt) {
      const addBtn = evt.target.closest('[data-ck-add]');
      if (!addBtn || !this.contains(addBtn)) return;
      evt.preventDefault();

      if (addBtn.dataset.hasVariants === 'true') {
        this.openVariantPicker(addBtn);
        return;
      }

      const variantId = addBtn.dataset.variantId;
      if (!variantId) return;

      this.addToCart(variantId, addBtn);
    }

    openVariantPicker(btn) {
      const handle = btn.dataset.productHandle;
      if (!handle) return;
      document.dispatchEvent(new CustomEvent('ck:cart-upsell:open-picker', {
        detail: {
          handle: handle,
          productId: btn.dataset.productId || null,
          originButton: btn
        }
      }));
    }

    addToCart(variantId, btn) {
      this.setBusy(btn, true);

      fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] })
      })
        .then((res) => {
          if (!res.ok) {
            return res.json().then((err) => { throw new Error((err && err.description) || res.statusText); });
          }
          return res.json();
        })
        .then(() => {
          document.dispatchEvent(new CustomEvent('cart:build'));
        })
        .catch((err) => {
          console.error('[ck-cart-upsell] add to cart failed', err);
          this.announce(this.errorString);
          this.setBusy(btn, false);
        });
    }

    setBusy(btn, busy) {
      if (!btn) return;
      if (busy) {
        btn.setAttribute('aria-busy', 'true');
        btn.disabled = true;
      } else {
        btn.setAttribute('aria-busy', 'false');
        btn.disabled = false;
      }
    }

    announce(msg) {
      if (!this.live) return;
      this.live.textContent = '';
      void this.live.offsetWidth;
      this.live.textContent = msg;
    }
  }

  CkCartUpsell.dismissedKeys = new Set();

  customElements.define('ck-cart-upsell', CkCartUpsell);
})();
